import { useCallback, useEffect, useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { Field, Loading } from "./common";
import { CERT_TEXT } from "./BerthSheet";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Checkbox } from "./ui/checkbox";
import { Button } from "./ui/button";

const EMPTY = { byPass: false, byPassAdj: "", grillo: false, grilloAdj: "", altro: "", descr: "", cert: false };

export function DarsenaSheet({ id }) {
  const [data, setData] = useState(null);
  const [f, setF] = useState(EMPTY);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => api(`/marina/darsena/${id}`).then(setData).catch((e) => toast.error(e.message || "Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const b = data.berth || {};

  const save = async () => {
    if (!f.byPass && !f.grillo && !f.altro.trim() && !f.descr.trim()) return toast.error("Inserisci almeno un intervento o una descrizione");
    if (f.byPass && !f.byPassAdj.trim()) return toast.error("Indica il posto barca adiacente (by pass)");
    if (f.grillo && !f.grilloAdj.trim()) return toast.error("Indica il posto barca adiacente (grillo)");
    if (!f.cert) return toast.error("Conferma la certificazione del Nostromo per proseguire");
    try {
      await api(`/marina/darsena/${id}/maintenance`, { method: "POST", body: { by_pass: f.byPass, by_pass_adiacente: f.byPassAdj.trim(), grillo: f.grillo, grillo_adiacente: f.grilloAdj.trim(), altro: f.altro.trim(), descrizione: f.descr.trim(), certifica: f.cert } });
      toast.success("Manutenzione darsena registrata"); setF(EMPTY); load();
    } catch (e) { toast.error(e.message); }
  };
  const saveEdit = async () => {
    try { await api(`/marina/darsena/${id}/maintenance/${edit.id}`, { method: "PUT", body: { descrizione: edit.descrizione.trim(), data_intervento: edit.data_intervento.trim() } }); toast.success("Intervento aggiornato"); setEdit(null); load(); } catch (e) { toast.error(e.message); }
  };
  const del = async (m) => {
    if (!window.confirm("Eliminare questo intervento?")) return;
    try { await api(`/marina/darsena/${id}/maintenance/${m.id}`, { method: "DELETE" }); toast.success("Intervento eliminato"); load(); } catch (e) { toast.error(e.message); }
  };

  return (
    <div className="space-y-5" data-testid="darsena-sheet">
      <div><p className="eyebrow">Darsena</p><h2 className="font-display text-2xl font-bold text-[var(--navy)]">{b.pontile} {b.numero || ""}</h2></div>
      {data.can_edit && (
        <div className="space-y-3 rounded-xl border p-4">
          <h3 className="font-semibold text-[var(--navy)]">Nuovo intervento</h3>
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.byPass} onCheckedChange={(v) => setF({ ...f, byPass: !!v })} data-testid="darsena-bypass" />Messo by pass</label>
          {f.byPass && <Input value={f.byPassAdj} onChange={(e) => setF({ ...f, byPassAdj: e.target.value })} placeholder="Posto barca adiacente es. A - 12" data-testid="darsena-bypass-adj" />}
          <label className="flex items-center gap-2 text-sm"><Checkbox checked={f.grillo} onCheckedChange={(v) => setF({ ...f, grillo: !!v })} data-testid="darsena-grillo" />Messo grillo su golfare del corpo morto</label>
          {f.grillo && <Input value={f.grilloAdj} onChange={(e) => setF({ ...f, grilloAdj: e.target.value })} placeholder="Posto barca adiacente es. A - 13" data-testid="darsena-grillo-adj" />}
          <Field label="Altro"><Input value={f.altro} onChange={(e) => setF({ ...f, altro: e.target.value })} placeholder="Altro intervento (testo libero)" data-testid="darsena-altro" /></Field>
          <Field label="Descrizione"><Textarea value={f.descr} onChange={(e) => setF({ ...f, descr: e.target.value })} data-testid="darsena-descr" /></Field>
          <label className="flex items-start gap-2 text-sm"><Checkbox checked={f.cert} onCheckedChange={(v) => setF({ ...f, cert: !!v })} data-testid="darsena-cert" />{data.cert_text || CERT_TEXT}</label>
          <Button onClick={save} className="rounded-full bg-[var(--teal)] w-full" data-testid="darsena-save">Registra intervento</Button>
        </div>
      )}
      <div><h3 className="font-semibold text-[var(--navy)] mb-2">Storico manutenzione settore {b.settore || ""}</h3>
        {(data.history || []).length === 0 ? <p className="text-sm text-slate-500">Nessun intervento.</p> : (
          <div className="space-y-2">{data.history.map((m) => (
            <div key={m.id} className="text-sm border rounded-lg p-3">
              <div className="flex justify-between gap-2"><div>{(m.voci || []).map((v, i) => <p key={i} className="font-medium">{v.tipo}{v.adiacente ? ` — adiacente: ${v.adiacente}` : ""}{v.testo ? `: ${v.testo}` : ""}</p>)}</div>
                {(data.can_edit || data.is_admin) && <div className="flex gap-1"><button onClick={() => setEdit({ id: m.id, descrizione: m.descrizione || "", data_intervento: m.data_intervento || "" })} className="text-slate-400" data-testid={`darsena-edit-${m.id}`}><Pencil className="w-4 h-4" /></button><button onClick={() => del(m)} className="text-slate-400 hover:text-[var(--red)]" data-testid={`darsena-delete-${m.id}`}><Trash2 className="w-4 h-4" /></button></div>}</div>
              {m.descrizione && <p className="text-slate-600 mt-1">{m.descrizione}</p>}
              <p className="text-xs text-slate-400 mt-1">{m.data_intervento} {m.orario || ""} · {m.operatore || ""}</p>
              {edit?.id === m.id && (
                <div className="space-y-2 mt-3 border-t pt-3">
                  <Input value={edit.data_intervento} onChange={(e) => setEdit({ ...edit, data_intervento: e.target.value })} placeholder="Data (aaaa-mm-gg)" />
                  <Textarea value={edit.descrizione} onChange={(e) => setEdit({ ...edit, descrizione: e.target.value })} placeholder="Descrizione intervento..." />
                  <div className="flex gap-2"><Button size="sm" onClick={saveEdit} className="rounded-full bg-[var(--teal)]">Salva</Button><Button size="sm" variant="ghost" onClick={() => setEdit(null)}>Annulla</Button></div>
                </div>)}
            </div>))}
          </div>)}
      </div>
    </div>
  );
}

export function LocaleSheet({ id }) {
  const [data, setData] = useState(null);
  const [tipo, setTipo] = useState("");
  const [descr, setDescr] = useState("");
  const load = useCallback(() => api(`/marina/locale/${id}`).then(setData).catch((e) => toast.error(e.message || "Errore caricamento")), [id]);
  useEffect(() => { load(); }, [load]);
  if (!data) return <Loading />;
  const l = data.locale || {};
  const save = async () => {
    if (!tipo.trim()) return toast.error("Inserisci il tipo di intervento");
    try { await api(`/marina/locale/${id}/maintenance`, { method: "POST", body: { tipo: tipo.trim(), descrizione: descr.trim() } }); toast.success("Intervento registrato"); setTipo(""); setDescr(""); load(); } catch (e) { toast.error(e.message); }
  };
  return (
    <div className="space-y-5" data-testid="locale-sheet">
      <div><p className="eyebrow">Locale</p><h2 className="font-display text-2xl font-bold text-[var(--navy)]">{l.nome}</h2>{l.edificio && <p className="text-sm text-slate-500">{l.edificio}</p>}</div>
      {data.can_edit ? (
        <div className="space-y-3 rounded-xl border p-4">
          <h3 className="font-semibold text-[var(--navy)]">Nuovo intervento</h3>
          <Field label="Tipo intervento"><Input value={tipo} onChange={(e) => setTipo(e.target.value)} data-testid="locale-tipo" /></Field>
          <Field label="Descrizione (opzionale)"><Textarea value={descr} onChange={(e) => setDescr(e.target.value)} data-testid="locale-descr" /></Field>
          <Button onClick={save} className="rounded-full bg-[var(--teal)] w-full" data-testid="locale-save">Registra intervento</Button>
        </div>
      ) : <p className="text-sm text-slate-500">Solo i Manutentori o il Direttore possono registrare interventi.</p>}
      <div><h3 className="font-semibold text-[var(--navy)] mb-2">Interventi registrati</h3>
        {(data.history || []).length === 0 ? <p className="text-sm text-slate-500">Nessun intervento.</p> : data.history.map((m, i) => (
          <div key={m.id || i} className="text-sm border rounded-lg p-3 mb-2"><p className="font-medium">{m.tipo}</p>{m.descrizione && <p className="text-slate-600">{m.descrizione}</p>}<p className="text-xs text-slate-400 mt-1">{m.data_intervento} {m.orario || ""} · {m.operatore || ""}</p></div>))}
      </div>
    </div>
  );
}
